import { useEffect, useState } from 'react';
import type { Tag } from '@epasu/core';
import { crm } from '@/lib/crm-client';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';

/**
 * Chips zum Auswählen mehrerer Tags, mit der Möglichkeit, einen neuen
 * anzulegen.
 *
 * Es gibt noch keine eigene Verwaltungsseite für Tags — die drei Standard-
 * Tags aus dem Seed sind sonst für immer alles, was zur Auswahl steht. Ein
 * neuer Tag entsteht deshalb direkt hier, dort wo er zum ersten Mal gebraucht
 * wird.
 */
interface Props {
  selected: string[];
  onChange: (tagIds: string[]) => void;
  disabled?: boolean;
}

/** Aus einem Label einen stabilen Schlüssel machen — Kleinbuchstaben, Bindestriche statt allem anderen. */
function schluesselAus(label: string): string {
  return (
    label
      .trim()
      .toLowerCase()
      .replace(/[äöüß]/g, (z) => ({ ä: 'ae', ö: 'oe', ü: 'ue', ß: 'ss' })[z] ?? z)
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/(^-+|-+$)/g, '') || 'tag'
  );
}

export function TagPicker({ selected, onChange, disabled }: Props) {
  const [tags, setTags] = useState<Tag[]>([]);
  const [neu, setNeu] = useState('');
  const [busy, setBusy] = useState(false);
  const [fehler, setFehler] = useState<string | null>(null);

  useEffect(() => {
    void crm.tags.list().then(setTags);
  }, []);

  const umschalten = (tagId: string) => {
    if (disabled) return;
    onChange(
      selected.includes(tagId)
        ? selected.filter((id) => id !== tagId)
        : [...selected, tagId],
    );
  };

  const anlegen = async () => {
    const label = neu.trim();
    if (!label) return;
    setBusy(true);
    setFehler(null);
    try {
      const tag = await crm.tags.create({ key: schluesselAus(label), label });
      setTags((bisher) => [...bisher, tag].sort((a, b) => a.label.localeCompare(b.label, 'de')));
      onChange([...selected, tag.id]);
      setNeu('');
    } catch (err) {
      setFehler(err instanceof Error ? err.message : 'Anlegen fehlgeschlagen');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-2">
      <div className="flex flex-wrap gap-1.5">
        {tags.length === 0 && (
          <span className="text-xs text-muted-foreground">Noch keine Tags angelegt.</span>
        )}
        {tags.map((t) => {
          const aktiv = selected.includes(t.id);
          return (
            <button
              key={t.id}
              type="button"
              disabled={disabled}
              onClick={() => umschalten(t.id)}
              aria-pressed={aktiv}
              className={cn(
                'rounded-full border px-2.5 py-1 text-xs font-medium transition-colors disabled:cursor-not-allowed disabled:opacity-50',
                aktiv
                  ? 'border-brand bg-brand-muted text-brand'
                  : 'border-border text-muted-foreground hover:bg-accent hover:text-accent-foreground',
              )}
            >
              {t.label}
            </button>
          );
        })}
      </div>

      {!disabled && (
        <div className="flex items-center gap-2">
          <Input
            value={neu}
            onChange={(e) => setNeu(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                void anlegen();
              }
            }}
            placeholder="Neuer Tag …"
            className="h-8 flex-1 text-xs"
          />
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => void anlegen()}
            disabled={busy || !neu.trim()}
          >
            Anlegen
          </Button>
        </div>
      )}

      {fehler && <p className="text-xs text-danger">{fehler}</p>}
    </div>
  );
}
